import type { UserStateMap } from '../../types/user-state';
import { isSavedRoleKey } from '../../types/user-state';
import { parseSavedRoleItemKey } from './saved-role-item-key';
import { parseWatchlistSeen, type WatchlistSeenSnapshot } from './watchlist-seen-snapshot';

export interface FollowedRole {
  /** Localized normalisedTitle exactly as the jobs index facets it. */
  roleTitle: string;
  /**
   * The locale whose jobs index this title belongs to — normalisedTitle facet
   * values are per-locale, so a title watched on /fi must be queried against
   * jobs_fi regardless of the UI language at read time.
   */
  locale: 'en' | 'fi' | 'sv';
  followedAt?: string;
  watchlistSeen?: WatchlistSeenSnapshot;
}

function parseRoleLocale(value: unknown): 'en' | 'fi' | 'sv' {
  return value === 'fi' || value === 'sv' ? value : 'en';
}

/**
 * Watched roles live in the same generic UserState map as followed companies —
 * `savedRole#*` entries, most-recently-watched first (mirror of
 * extract-followed-companies-from-user-state.ts).
 */
export function extractFollowedRolesFromUserState(userState: UserStateMap): FollowedRole[] {
  const roles: FollowedRole[] = [];

  for (const [itemKey, entry] of Object.entries(userState)) {
    if (!isSavedRoleKey(itemKey)) continue;
    const parsedTitle = parseSavedRoleItemKey(itemKey);
    if (!parsedTitle) continue;
    const data = entry.data as Record<string, unknown>;
    roles.push({
      roleTitle: typeof data.roleTitle === 'string' && data.roleTitle ? data.roleTitle : parsedTitle,
      locale: parseRoleLocale(data.locale),
      followedAt: typeof data.followedAt === 'string' ? data.followedAt : undefined,
      watchlistSeen: parseWatchlistSeen(data.watchlistSeen) ?? undefined,
    });
  }

  roles.sort((a, b) => (b.followedAt ?? '').localeCompare(a.followedAt ?? ''));
  return roles;
}
